import { Sparkles, UserCheck, ShieldQuestion } from 'lucide-react';

/**
 * Visual badges to indicate AI assistance + human review state on a chapter.
 *
 * Props:
 *  - reviewed: boolean — has the chapter been explicitly human-reviewed?
 *  - hasReview: boolean — was an AI reviewer ran on the chapter?
 *  - lang: 'bg' | 'en'
 */
export default function AIAssistanceBadge({ reviewed = false, hasReview = false, lang = 'bg' }) {
  const aiTooltip = lang === 'en'
    ? 'This content was AI-assisted. Verify before publishing.'
    : 'Това съдържание е генерирано с AI помощ. Проверете преди публикуване.';
  const humanTooltip = lang === 'en'
    ? (reviewed ? 'Author confirmed human review of this chapter.' : 'Human review required before export / publication.')
    : (reviewed ? 'Авторът потвърди ръчна проверка на главата.' : 'Изисква ръчна проверка от автора преди експорт / публикуване.');

  return (
    <span className="inline-flex items-center gap-1.5 flex-wrap" data-testid="ai-assistance-badges">
      <span
        title={aiTooltip}
        className="inline-flex items-center gap-1 rounded-full border border-teal-600/50 bg-teal-50 dark:bg-teal-950/40 text-teal-800 dark:text-teal-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
        data-testid="badge-ai-assisted"
      >
        <Sparkles className="w-3 h-3" />
        {lang === 'en' ? 'AI-assisted' : 'AI-асистирана'}
      </span>
      <span
        title={humanTooltip}
        className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${
          reviewed
            ? 'border-emerald-600/50 bg-emerald-50 dark:bg-emerald-950/40 text-emerald-800 dark:text-emerald-300'
            : 'border-rose-600/50 bg-rose-50 dark:bg-rose-950/40 text-rose-800 dark:text-rose-300'
        }`}
        data-testid={reviewed ? 'badge-human-reviewed' : 'badge-human-review-required'}
      >
        {reviewed ? <UserCheck className="w-3 h-3" /> : <ShieldQuestion className="w-3 h-3" />}
        {reviewed
          ? (lang === 'en' ? 'Human-reviewed' : 'Ръчно проверено')
          : (lang === 'en' ? 'Human review required' : 'Изисква ръчна проверка')}
      </span>
      {hasReview && (
        <span
          className="inline-flex items-center gap-1 rounded-full border border-sky-600/50 bg-sky-50 dark:bg-sky-950/40 text-sky-800 dark:text-sky-300 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider"
          title={lang === 'en' ? 'AI reviewer feedback available' : 'Налице е AI рецензия'}
        >
          {lang === 'en' ? 'AI review' : 'AI рецензия'}
        </span>
      )}
    </span>
  );
}
